import React, { Component, ErrorInfo, ReactNode } from 'react';
import defaultTheme from './styled-components-theme';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

/**
 * ThemeErrorBoundary props
 */
interface ThemeErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * ThemeErrorBoundary state
 */
interface ThemeErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ThemeErrorBoundary component
 * This component catches errors in the ThemeProvider and provides a fallback
 * 
 * @param props The component props
 * @returns The theme error boundary component
 */
class ThemeErrorBoundary extends Component<ThemeErrorBoundaryProps, ThemeErrorBoundaryState> {
  constructor(props: ThemeErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ThemeErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ThemeErrorBoundary caught an error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        // @ts-ignore - StyledThemeProvider is a valid JSX component
        <StyledThemeProvider theme={defaultTheme as any}>
          <div style={{ padding: '20px', color: 'red' }}>
            <h2>Theme Error</h2>
            <p>An error occurred in the theme provider.</p>
            <pre>{this.state.error?.message}</pre>
          </div>
        </StyledThemeProvider>
      );
    }

    return this.props.children;
  }
}

export default ThemeErrorBoundary;